import { Component, OnDestroy, Output, EventEmitter, Input } from '@angular/core';
import { Subject, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SpeechService } from '../../services/speech.service';

@Component({
  selector: 'db-voice-recording',
  templateUrl: './voice-recording.component.html',
  styleUrls: ['./voice-recording.component.scss'],
  standalone: false
})
export class VoiceRecordingComponent implements OnDestroy {
  @Input() disabled: boolean = false;
  @Output() transcriptUpdate = new EventEmitter<string>();
  @Output() recordingStateChange = new EventEmitter<boolean>();
  @Output() isRecordingChange = new EventEmitter<boolean>();

  private _isRecording = false;

  get isRecording(): boolean {
    return this._isRecording;
  }

  set isRecording(value: boolean) {
    this._isRecording = value;
    this.isRecordingChange.emit(value);
  }

  public recordingDuration = 0;
  public audioBars: number[] = [];
  public disabledBars: number[] = [];
  private recordingInterval$?: any;
  private destroy$ = new Subject<void>();

  constructor(private speechService: SpeechService) {}

  ngOnDestroy(): void {
    this.stopRecording();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public async toggleRecording(): Promise<void> {
    if (this.disabled) {
      return;
    }

    if (this.isRecording) {
      this.stopRecording();
    } else {
      await this.startRecording();
    }
  }

  private async startRecording(): Promise<void> {
    const hasPermission = await this.speechService.checkMicrophonePermission();
    if (!hasPermission) {
      alert('Microphone permission is required for voice input');
      return;
    }

    this.isRecording = true;
    this.recordingDuration = 0;
    this.audioBars = [];
    // Initialize placeholder bars with opacity 0.5 until real bars show up
    this.disabledBars = Array.from({ length: 105 }, () => 10);
    this.recordingStateChange.emit(true);

    // Start duration timer
    this.recordingInterval$ = interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.recordingDuration++;
      });

    let smoothLevel = 0; // keep across callback calls
    // Start audio analysis for visualization
    this.speechService.startAudioAnalysis((frequency: number) => {
      // 0..1
      const normalized = frequency / 255;

      // Raise silence threshold a bit so background noise doesn't look like speaking
      const silenceThreshold = 0.12;

      // Smooth the signal so bars don't jitter (0.0 = no smoothing, 0.9 = heavy smoothing)
      const smoothing = 0.75;
      smoothLevel = smoothLevel * smoothing + normalized * (1 - smoothing);

      let height: number;

      if (smoothLevel < silenceThreshold) {
        // SILENT: keep it small and steady
        // (tiny movement so it doesn't look "dead")
        height = 6 + smoothLevel * 20; // ~6..8-10
      } else {
        // TALKING: map remaining range to a much bigger height
        const adjusted = (smoothLevel - silenceThreshold) / (1 - silenceThreshold); // 0..1

        // Use a curve that keeps quiet speech lower, loud speech MUCH higher
        // power > 1 makes low values smaller and high values bigger
        const curved = Math.pow(adjusted, 2.2);

        const minTalkingHeight = 5;
        const maxHeight = 110;
        height = minTalkingHeight + curved * (maxHeight - minTalkingHeight);
      }

      // Clamp and push
      height = Math.max(6, Math.min(110, height));
      this.audioBars.push(height);

      // Remove placeholder bars from the left as real bars come in
      if (this.disabledBars.length > 0) {
        this.disabledBars.shift();
      }

      // Keep last N bars
      const MAX_BARS = 107;
      if (this.audioBars.length > MAX_BARS) {
        this.audioBars.shift();
      }
    });
    // Start speech recognition
    this.speechService.startSpeechRecognition((transcript: string, isFinal: boolean) => {
      if (isFinal) {
        this.transcriptUpdate.emit(transcript);
      }
    });
  }

  public stopRecording(): void {
    if (!this.isRecording) {
      return;
    }

    this.isRecording = false;
    this.recordingStateChange.emit(false);

    if (this.recordingInterval$) {
      this.recordingInterval$.unsubscribe();
      this.recordingInterval$ = undefined;
    }

    this.speechService.stopSpeechRecognition();
    this.speechService.stopAudioAnalysis();

    // Move active bars to disabled, keep them in order from left to right (keep last 107 bars)
    this.disabledBars = [...this.disabledBars, ...this.audioBars].slice(-107);
    this.audioBars = [];
    this.recordingDuration = 0;
  }

  get formattedDuration(): string {
    const minutes = Math.floor(this.recordingDuration / 60);
    const seconds = this.recordingDuration % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
}
